// Financial (GL) account — minimal shape for the AP line GL-account picker.
// Mirrors Finance.API FinancialAccountDto.
import { ResponseTranslation } from '../Translations';

export enum AccountType {
  Asset = 1,
  Liability = 2,
  Equity = 3,
  Revenue = 4,
  Expense = 5,
}

/** Party-subledger a control account reconciles against. Mirrors Finance.API SubledgerType. */
export enum SubledgerType {
  Provider = 1,
  Client = 2,
  Bank = 3,
  InternalCompany = 4,
  /** Employee advances and payables (#5777); the party is an EmployeeAccount. */
  Employee = 5,
  /** Petty cash fund cash (#5785), bank style; the party is the fund and several funds may share one account. */
  PettyCashFund = 6,
}

/** Modules allowed to post to an account. Mirrors Finance.API PostingModule. A subledger account must
 * authorize at least one module (controlled-account invariant). */
export enum PostingModule {
  GeneralLedger = 1,
  AccountsPayable = 2,
  AccountsReceivable = 3,
  Banking = 4,
  PettyCash = 5,
  FixedAssets = 6,
  /** Accounts usable on Cash Request lines (#4314). */
  CashManagement = 7,
}

export interface FinancialAccount {
  id?: string;
  accountNumber: string;
  accountName: ResponseTranslation;
  description?: ResponseTranslation | null;
  accountType: AccountType;
  financialCompanyReference: string;
  parentAccountId?: string | null;
  parentAccountNumber?: string | null;
  level?: number;
  isPostingAccount: boolean;
  isBlocked: boolean;
  isReconcilable?: boolean;
  /** Party-subledger for control accounts (Provider/Client/Bank); null for ordinary accounts. */
  subledgerType?: SubledgerType | null;
  /** Modules allowed to post here. Required (non-empty) when subledgerType is set. */
  authorizedPostingModules?: PostingModule[];
  /** Linked physical bank account id; null/empty = open (bank GL accounts only). */
  bankAccountReference?: string | null;
  /**
   * Posted GL balance (rolled up to group accounts on read), or `null` when the API REDACTED it —
   * a bank GL account whose balance this user has no grant for, or any ancestor of one.
   *
   * Distinct from `undefined` (field absent) and from `0` (a genuinely empty account). Render through
   * `| maskedAmount`; never coalesce to 0.
   */
  balance?: number | null;
  /**
   * Account currency. ASYMMETRIC: reads return the Currency enum as a NUMBER (the DTO types it as the
   * enum and JsonStringEnumConverter is disabled server-side), while writes bind the entity's `string`
   * and must send the code ("HNL"). Use `currencyFromApi` to read and `currencyLabel` to write.
   */
  currency?: number | string;
}

export interface FinancialAccountSearchCommand {
  accountNumber?: string | null;
  accountName?: string | null;
  accountType?: AccountType | null;
  financialCompanyReference?: string | null;
  parentAccountId?: string | null;
  isPostingAccount?: boolean | null;
  isBlocked?: boolean | null;
  subledgerType?: SubledgerType | null;
  /** When true, only bank GL accounts not yet linked to a bank account. */
  unlinkedBankOnly?: boolean | null;
}
